/**
 * Accountant Registration
 * Form to register as an accountant in the marketplace
 */

import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { trpc } from "@/lib/trpc";
import {
  Building2,
  MapPin,
  Mail,
  Phone,
  Globe,
  Award,
  Briefcase,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { getLoginUrl } from "@/const";

// Swiss cantons
const SWISS_CANTONS = [
  { value: "ZH", label: "Zürich" },
  { value: "BE", label: "Bern" },
  { value: "LU", label: "Luzern" },
  { value: "ZG", label: "Zug" },
  { value: "BS", label: "Basel-Stadt" },
  { value: "BL", label: "Basel-Landschaft" },
  { value: "SG", label: "St. Gallen" },
  { value: "AG", label: "Aargau" },
  { value: "GE", label: "Genève" },
  { value: "VD", label: "Vaud" },
  { value: "TI", label: "Ticino" },
  { value: "GR", label: "Graubünden" },
  // Add more as needed
];

const SPECIALIZATIONS = [
  {
    value: "startups",
    label: "Startups & Scale-ups",
    description: "Early-stage companies and growth",
  },
  {
    value: "r&d",
    label: "R&D Tax Credits",
    description: "Research and development incentives",
  },
  {
    value: "international",
    label: "International Tax",
    description: "Cross-border taxation",
  },
  {
    value: "vat",
    label: "VAT Specialist",
    description: "Value added tax expertise",
  },
  {
    value: "payroll",
    label: "Payroll & HR",
    description: "Employee administration",
  },
  {
    value: "audit",
    label: "Audit Services",
    description: "Financial auditing",
  },
  {
    value: "succession",
    label: "Business Succession",
    description: "Ownership transitions",
  },
  {
    value: "real-estate",
    label: "Real Estate",
    description: "Property taxation",
  },
];

const INDUSTRIES = [
  { value: "tech", label: "Technology" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "retail", label: "Retail" },
  { value: "healthcare", label: "Healthcare" },
  { value: "finance", label: "Finance" },
  { value: "hospitality", label: "Hospitality" },
  { value: "construction", label: "Construction" },
  { value: "professional", label: "Professional Services" },
];

const CERTIFICATIONS = [
  { value: "expert_suisse", label: "Expert Suisse" },
  { value: "treuhand_suisse", label: "Treuhand Suisse" },
  { value: "cpa", label: "CPA" },
  { value: "swiss_gaap", label: "Swiss GAAP FER Certified" },
];

export default function AccountantRegistration() {
  return (
    <DashboardLayout>
      <RegistrationContent />
    </DashboardLayout>
  );
}

function RegistrationContent() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const [step, setStep] = useState(1);

  // Form state
  const [formData, setFormData] = useState({
    firmName: "",
    firmType: "solo" as "solo" | "partnership" | "firm",
    swissUid: "",
    bio: "",
    website: "",
    contactEmail: "",
    contactPhone: "",
    address: {
      street: "",
      city: "",
      zip: "",
      canton: "",
    },
    cantons: [] as string[],
    specializations: [] as string[],
    industries: [] as string[],
    certifications: [] as string[],
    isPublic: true,
    acceptingClients: true,
  });

  // Check if user already has a profile
  const { data: existingProfile, isLoading: profileLoading } =
    trpc.accountant.myProfile.useQuery();

  const registerMutation = trpc.accountant.register.useMutation({
    onSuccess: () => {
      toast.success("Registration successful!", {
        description: "Your accountant profile has been created.",
      });
      setLocation("/accountant/dashboard");
    },
    onError: error => {
      toast.error("Registration failed", {
        description: error.message,
      });
    },
  });

  // Redirect if not authenticated
  if (!authLoading && !isAuthenticated) {
    return (
      <Card className="max-w-lg mx-auto">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Sign in Required</h3>
          <p className="text-sm text-muted-foreground text-center mb-4">
            Please sign in to register as an accountant.
          </p>
          <a href={getLoginUrl()}>
            <Button>Sign In</Button>
          </a>
        </CardContent>
      </Card>
    );
  }

  // Redirect if already registered
  if (existingProfile) {
    return (
      <Card className="max-w-lg mx-auto">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <CheckCircle2 className="h-12 w-12 text-[var(--success)] mb-4" />
          <h3 className="text-lg font-semibold mb-2">Already Registered</h3>
          <p className="text-sm text-muted-foreground text-center mb-4">
            You already have an accountant profile.
          </p>
          <Button onClick={() => setLocation("/accountant/dashboard")}>
            Go to Dashboard
          </Button>
        </CardContent>
      </Card>
    );
  }

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateAddress = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  const toggleArrayItem = (
    field: "cantons" | "specializations" | "industries" | "certifications",
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value],
    }));
  };

  const handleSubmit = () => {
    registerMutation.mutate({
      firmName: formData.firmName,
      firmType: formData.firmType,
      swissUid: formData.swissUid || undefined,
      bio: formData.bio || undefined,
      website: formData.website || undefined,
      contactEmail: formData.contactEmail || undefined,
      contactPhone: formData.contactPhone || undefined,
      address: formData.address.street ? formData.address : undefined,
      cantons: formData.cantons.length > 0 ? formData.cantons : undefined,
      specializations:
        formData.specializations.length > 0
          ? formData.specializations
          : undefined,
      industries:
        formData.industries.length > 0 ? formData.industries : undefined,
      certifications:
        formData.certifications.length > 0
          ? formData.certifications
          : undefined,
      isPublic: formData.isPublic,
      acceptingClients: formData.acceptingClients,
    });
  };

  const canProceed = () => {
    if (step === 1) {
      return formData.firmName.trim().length > 0;
    }
    return true;
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          onClick={() => setLocation("/accountants")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Marketplace
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">
          Become an Accountant
        </h1>
        <p className="text-muted-foreground">
          Register your firm to connect with clients looking for tax and
          accounting services.
        </p>
      </div>

      {/* Progress */}
      <div className="flex items-center justify-between">
        {[1, 2, 3].map(s => (
          <div key={s} className="flex items-center">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full ${
                s <= step
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {s}
            </div>
            {s < 3 && (
              <div
                className={`h-1 w-24 mx-2 ${s < step ? "bg-primary" : "bg-muted"}`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Basic Info */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Firm Information
            </CardTitle>
            <CardDescription>
              Tell us about your accounting firm or practice.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="firmName">Firm Name *</Label>
                <Input
                  id="firmName"
                  placeholder="e.g., Müller Treuhand AG"
                  value={formData.firmName}
                  onChange={e => updateField("firmName", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="firmType">Firm Type</Label>
                <Select
                  value={formData.firmType}
                  onValueChange={v => updateField("firmType", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solo">Solo Practitioner</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="firm">Full-Service Firm</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="swissUid">Swiss UID (Optional)</Label>
                <Input
                  id="swissUid"
                  placeholder="CHE-123.456.789"
                  value={formData.swissUid}
                  onChange={e => updateField("swissUid", e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Your firm's Unternehmens-Identifikationsnummer
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">About Your Firm</Label>
                <Textarea
                  id="bio"
                  placeholder="Describe your services, expertise, and what makes your firm unique..."
                  value={formData.bio}
                  onChange={e => updateField("bio", e.target.value)}
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  {formData.bio.length}/2000 characters
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Expertise */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Expertise & Services
            </CardTitle>
            <CardDescription>
              Select your areas of specialization and certifications.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Service Cantons */}
            <div className="space-y-3">
              <Label>Service Areas (Cantons)</Label>
              <p className="text-sm text-muted-foreground">
                Select the cantons where you offer services.
              </p>
              <div className="flex flex-wrap gap-2">
                {SWISS_CANTONS.map(canton => (
                  <Badge
                    key={canton.value}
                    variant={
                      formData.cantons.includes(canton.value)
                        ? "default"
                        : "outline"
                    }
                    className="cursor-pointer"
                    onClick={() => toggleArrayItem("cantons", canton.value)}
                  >
                    <MapPin className="mr-1 h-3 w-3" />
                    {canton.label}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Specializations */}
            <div className="space-y-3">
              <Label>Specializations</Label>
              <div className="grid gap-2 md:grid-cols-2">
                {SPECIALIZATIONS.map(spec => (
                  <div
                    key={spec.value}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.specializations.includes(spec.value)
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/50"
                    }`}
                    onClick={() =>
                      toggleArrayItem("specializations", spec.value)
                    }
                  >
                    <Checkbox
                      checked={formData.specializations.includes(spec.value)}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="font-medium text-sm">{spec.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {spec.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Industries */}
            <div className="space-y-3">
              <Label>Industries Served</Label>
              <div className="flex flex-wrap gap-2">
                {INDUSTRIES.map(ind => (
                  <Badge
                    key={ind.value}
                    variant={
                      formData.industries.includes(ind.value)
                        ? "default"
                        : "outline"
                    }
                    className="cursor-pointer"
                    onClick={() => toggleArrayItem("industries", ind.value)}
                  >
                    {ind.label}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Certifications */}
            <div className="space-y-3">
              <Label>Certifications</Label>
              <div className="flex flex-wrap gap-2">
                {CERTIFICATIONS.map(cert => (
                  <Badge
                    key={cert.value}
                    variant={
                      formData.certifications.includes(cert.value)
                        ? "default"
                        : "outline"
                    }
                    className="cursor-pointer"
                    onClick={() =>
                      toggleArrayItem("certifications", cert.value)
                    }
                  >
                    <Award className="mr-1 h-3 w-3" />
                    {cert.label}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Contact & Settings */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Contact & Settings
            </CardTitle>
            <CardDescription>
              How potential clients can reach you.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Contact Info */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="contactEmail">Contact Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="contactEmail"
                    type="email"
                    placeholder="contact@yourfirm.ch"
                    value={formData.contactEmail}
                    onChange={e => updateField("contactEmail", e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactPhone">Contact Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="contactPhone"
                    type="tel"
                    placeholder="+41 44 123 45 67"
                    value={formData.contactPhone}
                    onChange={e => updateField("contactPhone", e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="website">Website</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="website"
                    type="url"
                    placeholder="https://www.yourfirm.ch"
                    value={formData.website}
                    onChange={e => updateField("website", e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="space-y-4">
              <Label>Office Address</Label>
              <div className="grid gap-4">
                <Input
                  placeholder="Street Address"
                  value={formData.address.street}
                  onChange={e => updateAddress("street", e.target.value)}
                />
                <div className="grid gap-4 md:grid-cols-3">
                  <Input
                    placeholder="ZIP Code"
                    value={formData.address.zip}
                    onChange={e => updateAddress("zip", e.target.value)}
                  />
                  <Input
                    placeholder="City"
                    value={formData.address.city}
                    onChange={e => updateAddress("city", e.target.value)}
                  />
                  <Select
                    value={formData.address.canton}
                    onValueChange={v => updateAddress("canton", v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Canton" />
                    </SelectTrigger>
                    <SelectContent>
                      {SWISS_CANTONS.map(canton => (
                        <SelectItem key={canton.value} value={canton.value}>
                          {canton.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Settings */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-medium">Profile Settings</h4>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">Public Profile</p>
                  <p className="text-xs text-muted-foreground">
                    Show your profile in the marketplace
                  </p>
                </div>
                <Switch
                  checked={formData.isPublic}
                  onCheckedChange={v => updateField("isPublic", v)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">Accepting New Clients</p>
                  <p className="text-xs text-muted-foreground">
                    Let clients know you're available
                  </p>
                </div>
                <Switch
                  checked={formData.acceptingClients}
                  onCheckedChange={v => updateField("acceptingClients", v)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setStep(Math.max(1, step - 1))}
          disabled={step === 1}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Previous
        </Button>

        {step < 3 ? (
          <Button onClick={() => setStep(step + 1)} disabled={!canProceed()}>
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={registerMutation.isPending}>
            {registerMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Registering...
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Complete Registration
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
